import { Link } from 'react-router-dom';
import type { SessionState } from '../../api';
import { Section } from './_ui';
import { paymentsEnabled } from '../../config';

// Reading Economy v2 §F9, STA-124 — follow-up panel for the done state.
type FollowupPanelProps = {
  session: SessionState;
  mentorName?: string | null;
  userStardust: number;
  draft: string;
  setDraft: (v: string) => void;
  busy: boolean;
  streamText: string;
  error: string | null;
  onSubmit: (q: string) => void;
};

export function FollowupPanel({
  session,
  mentorName,
  userStardust,
  draft,
  setDraft,
  busy,
  streamText,
  error,
  onSubmit,
}: FollowupPanelProps) {
  const meta = session.followup_meta;
  const followups = session.followups ?? [];

  // Async mentor or pre-complete session → panel is not applicable.
  if (!meta) {
    return (
      <Section title="繼續問老師">
        <div className="text-tarot-muted text-sm">
          這位老師暫不支援追問。
        </div>
      </Section>
    );
  }

  const now = Date.now();
  const expiresAt = meta.window_expires_at ? new Date(meta.window_expires_at).getTime() : null;
  const remainingMs = expiresAt ? expiresAt - now : 0;
  const windowExpired = expiresAt !== null && remainingMs <= 0;
  const quotaExhausted = meta.remaining <= 0;
  const cost = meta.user_pays_zero ? 0 : meta.cost_stardust;
  const insufficient = !meta.user_pays_zero && userStardust < cost;

  const disabled = busy || quotaExhausted || windowExpired || insufficient || draft.trim().length === 0;

  let disabledReason: string | null = null;
  if (quotaExhausted) disabledReason = '本次占卜已用完 3 次追問。';
  else if (windowExpired) disabledReason = '追問時效已過。';
  else if (insufficient) disabledReason = `需要 ${cost}★（餘額 ${userStardust}★）。`;

  const minutesLeft = expiresAt ? Math.max(0, Math.floor(remainingMs / 60000)) : null;

  return (
    <Section title="繼續問老師">
      {/* Conversation thread */}
      {followups.length > 0 && (
        <div className="space-y-3 mb-6">
          {followups
            .filter((f) => f.status !== 'refunded')
            .sort((a, b) => a.sequence - b.sequence)
            .map((f) => (
              <div key={f.id} className="space-y-2">
                <div className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl bg-tarot-purple/20 border border-tarot-purple/40 px-3 py-2 text-sm text-tarot-text">
                    <div className="text-[10px] uppercase tracking-wider text-tarot-muted mb-0.5">
                      Q{f.sequence} {f.was_free ? '· Premium 免費' : `· ${f.stardust_cost}★`}
                    </div>
                    {f.question}
                  </div>
                </div>
                <div className="flex justify-start">
                  <div className="max-w-[90%] card !p-3 text-sm whitespace-pre-wrap">
                    {f.answer_text || '（無回應）'}
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Live streaming preview */}
      {busy && (
        <div className="space-y-2 mb-6">
          <div className="flex justify-end">
            <div className="max-w-[85%] rounded-2xl bg-tarot-purple/20 border border-tarot-purple/40 px-3 py-2 text-sm text-tarot-text">
              <div className="text-[10px] uppercase tracking-wider text-tarot-muted mb-0.5">
                Q{(meta.used ?? 0) + 1}（傳送中…）
              </div>
              {draft}
            </div>
          </div>
          <div className="flex justify-start">
            <div className="max-w-[90%] card !p-3 text-sm whitespace-pre-wrap leading-relaxed">
              {streamText || (
                <span className="text-tarot-muted">{mentorName ? `${mentorName} 正在凝神…` : '老師正在凝神…'}</span>
              )}
              {streamText && <span className="text-tarot-gold">▍</span>}
            </div>
          </div>
        </div>
      )}

      {/* Composer */}
      {!quotaExhausted && !windowExpired && (
        <div>
          <div className="flex items-center justify-between text-xs text-tarot-muted mb-2">
            <span>
              已用 {meta.used} / {meta.max}
              {minutesLeft !== null && (
                <span className="ml-3">⏳ 剩 {minutesLeft >= 1 ? `${minutesLeft} 分` : '< 1 分'}</span>
              )}
            </span>
            <span className={insufficient ? 'text-red-400' : 'text-tarot-gold'}>
              {meta.user_pays_zero
                ? 'Premium 免費追問'
                : `每次 ${cost}★（餘額 ${userStardust}★）`}
            </span>
          </div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="例：如果我選擇留下來，未來三個月的關鍵點是什麼？"
            className="input min-h-[6rem]"
            maxLength={500}
            disabled={busy}
          />
          {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
          {disabledReason && !busy && <p className="text-tarot-muted text-sm mt-2">{disabledReason}</p>}
          <div className="flex justify-end mt-3">
            <button
              type="button"
              onClick={() => onSubmit(draft.trim())}
              disabled={disabled}
              className="btn-primary"
            >
              {busy ? '請老師思考中…' : meta.user_pays_zero ? '繼續問（免費）' : `繼續問（扣 ${cost}★）`}
            </button>
          </div>
          {insufficient && (
            <p className="text-xs text-tarot-muted mt-2">
              <Link to="/account">前往帳號頁</Link>
              {paymentsEnabled ? (
                <> 或升級 <Link to="/pricing">Premium</Link> 免費追問。</>
              ) : (
                <> 累積更多 Stardust（Premium 訂閱即將上線）。</>
              )}
            </p>
          )}
        </div>
      )}

      {quotaExhausted && !windowExpired && (
        <p className="text-tarot-muted text-sm">本次占卜的 3 次追問已用完。</p>
      )}
      {windowExpired && (
        <p className="text-tarot-muted text-sm">追問時效已過（解讀後 1 小時內可追問）。</p>
      )}
    </Section>
  );
}
